import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_MEDIA_API ?? '/api/v1';

export type AssetStatus = 'draft' | 'pending_approval' | 'approved' | 'published' | 'archived';
export type AssetType = 'image' | 'video' | 'audio' | 'document' | 'ai_generated' | 'other';

export type Asset = {
  id: string;
  tenant_id: string;
  organization_id: string;
  name: string;
  description?: string;
  type: AssetType;
  status: AssetStatus;
  mime_type?: string;
  file_url?: string;
  thumbnail_url?: string;
  file_size?: number;
  checksum?: string;
  tags?: string[];
  version: number;
  created_at: string;
  updated_at: string;
};

export type Prompt = {
  id: string;
  name: string;
  content: string;
  negative_prompt?: string;
  model?: string;
  is_template?: boolean;
  category?: string;
  status?: string;
  created_at: string;
  updated_at: string;
};

export type AssetVersion = { id: string; asset_id: string; version: number; file_url?: string; transform?: string; created_at: string };
export type DuplicateGroup = { checksum: string; assets: Asset[]; count: number };
export type QualityReport = { asset_id: string; score: number; status: string; issues?: string[]; checks?: Record<string, boolean> };
export type AIGeneration = { id: string; prompt_id: string; status: string; asset_id?: string; output_url?: string; created_at: string };

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function mediaFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Media ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const assetApi = {
  list: (params?: { status?: string; q?: string; type?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    return mediaFetch<Asset[]>(`/assets${query ? `?${query}` : ''}`);
  },
  search: (q: string) => mediaFetch<{ results: Asset[]; count: number }>(`/assets/search?q=${encodeURIComponent(q)}`),
  get: (id: string) => mediaFetch<Asset>(`/assets/${id}`),
  create: (input: Partial<Asset> & { name: string; file_url?: string }) =>
    mediaFetch<Asset>('/assets', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Asset>) =>
    mediaFetch<Asset>(`/assets/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  delete: (id: string) => mediaFetch<void>(`/assets/${id}`, { method: 'DELETE' }),
  approve: (id: string, input?: { reject?: boolean }) =>
    mediaFetch<Asset>(`/assets/${id}/approve`, { method: 'POST', body: JSON.stringify(input ?? {}) }),
  publish: (id: string) => mediaFetch<Asset>(`/assets/${id}/publish`, { method: 'POST' }),
  archive: (id: string) => mediaFetch<Asset>(`/assets/${id}/archive`, { method: 'POST' }),
  restore: (id: string) => mediaFetch<Asset>(`/assets/${id}/restore`, { method: 'POST' }),
  bulkUpload: (items: Array<{ name: string; file_url: string; type?: AssetType }>) =>
    mediaFetch<{ created: Asset[]; count: number }>('/assets/bulk-upload', { method: 'POST', body: JSON.stringify({ items }) }),
  bulkDelete: (ids: string[]) =>
    mediaFetch<{ deleted: string[]; count: number }>('/assets/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  duplicates: () => mediaFetch<DuplicateGroup[]>('/assets/duplicates'),
  versions: (id: string) => mediaFetch<AssetVersion[]>(`/assets/${id}/versions`),
  transform: (id: string, input: { operation: string; width?: number; height?: number; format?: string }) =>
    mediaFetch<{ asset_id: string; file_url: string }>(`/assets/${id}/transform`, { method: 'POST', body: JSON.stringify(input) }),
  quality: (id: string) => mediaFetch<QualityReport>(`/assets/${id}/quality`),
};

export const promptApi = {
  list: () => mediaFetch<Prompt[]>('/prompts'),
  templates: () => mediaFetch<Prompt[]>('/prompts/templates'),
  create: (input: Partial<Prompt> & { name: string; content: string }) =>
    mediaFetch<Prompt>('/prompts', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Prompt>) =>
    mediaFetch<Prompt>(`/prompts/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  generate: (input: { prompt_id: string; create_asset?: boolean; parameters?: Record<string, unknown> }) =>
    mediaFetch<AIGeneration>('/prompts/generate', { method: 'POST', body: JSON.stringify(input) }),
};
